import React, { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { Plug, Download, RefreshCw, Loader2, CheckCircle2, XCircle, Users, Clock, Trash2 } from "lucide-react";

export default function IntegrationsPage() {
  const [status, setStatus] = useState(null);
  const [groups, setGroups] = useState([]);
  const [scheduled, setScheduled] = useState([]);
  const [loading, setLoading] = useState(true);
  const [importing, setImporting] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [p, g, s] = await Promise.all([
        api.get("/admin/integrations/smshosting/ping"),
        api.get("/admin/integrations/smshosting/groups"),
        api.get("/admin/integrations/smshosting/campaign/sms/scheduled").catch(() => ({ data: [] })),
      ]);
      setStatus(p.data);
      setGroups(g.data || []);
      setScheduled(s.data || []);
    } catch (e) {
      toast.error("Errore caricamento integrazione");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const cancelScheduled = async (id) => {
    if (!confirm("Annullare questa campagna programmata?")) return;
    try {
      await api.delete(`/admin/integrations/smshosting/campaign/sms/scheduled/${id}`);
      toast.success("Campagna annullata");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Annullamento fallito");
    }
  };

  const doImport = async (group) => {
    if (!confirm(`Importare ${group.contactCount} contatti dal gruppo "${group.name}"?`)) return;
    setImporting(group.id);
    try {
      const { data } = await api.post("/admin/integrations/smshosting/import", {
        group_id: group.id,
        limit: 1000,
      });
      toast.success(`Import: +${data.created} nuovi, ${data.updated} aggiornati, ${data.skipped} skip`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Import fallito");
    } finally {
      setImporting(null);
    }
  };

  return (
    <div data-testid="integrations-page">
      <div className="mb-8">
        <div className="font-jp text-yellow-400 text-xs tracking-widest">統合</div>
        <h1 className="font-heading text-4xl uppercase text-white">Integrazioni</h1>
        <p className="text-zinc-400 mt-2 text-sm">Sincronizza il CRM con piattaforme esterne.</p>
      </div>

      <section className="bg-zinc-900 border-2 border-zinc-800">
        <header className="px-5 py-4 bg-zinc-950 border-b-2 border-zinc-800 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="bg-yellow-400 text-black p-2"><Plug className="w-5 h-5" strokeWidth={3} /></div>
            <div>
              <div className="font-heading uppercase text-white text-lg">Smshosting.it</div>
              <div className="text-[11px] text-zinc-500 uppercase tracking-widest">Fidelity Card + SMS + Email</div>
            </div>
          </div>
          {loading ? (
            <Loader2 className="w-5 h-5 animate-spin text-zinc-500" />
          ) : status?.ok ? (
            <div className="flex items-center gap-2 text-green-400 text-xs font-heading uppercase tracking-widest" data-testid="status-ok">
              <CheckCircle2 className="w-4 h-4" /> Connesso · {status.username} ({status.plan})
            </div>
          ) : (
            <div className="flex items-center gap-2 text-red-400 text-xs font-heading uppercase tracking-widest" data-testid="status-fail">
              <XCircle className="w-4 h-4" /> Errore
            </div>
          )}
        </header>

        <div className="p-5 space-y-4">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <div className="font-heading uppercase text-white text-sm flex items-center gap-2">
                <Users className="w-4 h-4 text-yellow-400" /> Gruppi su Smshosting
              </div>
              <div className="text-xs text-zinc-500 mt-1">Clicca &quot;Importa&quot; per portare tutti i contatti del gruppo nel CRM.</div>
            </div>
            <button
              data-testid="reload-groups"
              onClick={load}
              className="border-2 border-zinc-700 hover:border-yellow-400 text-zinc-300 hover:text-yellow-400 px-3 py-2 text-xs font-heading uppercase tracking-widest flex items-center gap-2"
            >
              <RefreshCw className="w-4 h-4" strokeWidth={3} /> Ricarica
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {groups.length === 0 && !loading && (
              <div className="text-zinc-500 text-sm italic">Nessun gruppo trovato</div>
            )}
            {groups.map((g) => {
              const isCard = (g.name || "").toLowerCase().includes("card daitanjack");
              return (
                <div key={g.id} data-testid={`smsh-group-${g.id}`} className="bg-zinc-950 border-2 border-zinc-800 p-4">
                  <div className="flex items-start justify-between gap-2 mb-3">
                    <div className="min-w-0">
                      <div className="font-heading uppercase text-white text-sm truncate">{g.name}</div>
                      <div className="text-[10px] text-zinc-500 uppercase tracking-widest mt-0.5">ID: {g.id}</div>
                    </div>
                    {isCard && (
                      <span className="bg-yellow-400 text-black text-[9px] font-heading uppercase tracking-widest px-1.5 py-0.5">CARD</span>
                    )}
                  </div>
                  <div className="text-yellow-400 font-mono text-2xl mb-3">{g.contactCount}</div>
                  <button
                    data-testid={`import-group-${g.id}`}
                    onClick={() => doImport(g)}
                    disabled={!g.contactCount || importing === g.id}
                    className="w-full flex items-center justify-center gap-2 bg-yellow-400 text-black font-heading uppercase tracking-widest text-xs py-2 hover:bg-white transition disabled:opacity-40"
                  >
                    {importing === g.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" strokeWidth={3} />}
                    {isCard ? "Importa come Clienti" : "Importa come Lead"}
                  </button>
                </div>
              );
            })}
          </div>

          <div className="mt-6 pt-5 border-t-2 border-zinc-800">
            <div className="font-heading uppercase text-white text-sm mb-2 flex items-center gap-2">
              <Clock className="w-4 h-4 text-yellow-400" strokeWidth={3} /> Campagne SMS programmate
            </div>
            {scheduled.length === 0 ? (
              <div className="text-xs text-zinc-500">Nessuna campagna in coda. Programma una campagna dalla Pipeline.</div>
            ) : (
              <ul className="divide-y divide-zinc-800 border-2 border-zinc-800" data-testid="scheduled-list">
                {scheduled.map((s) => {
                  const isPending = s.status === "pending";
                  const sentBadge = s.status === "sent"
                    ? `✓ ${s.sent}/${s.targets} inviati`
                    : s.status === "canceled" ? "annullata"
                    : s.status === "sending" ? "in corso…" : "in attesa";
                  return (
                    <li key={s.id} data-testid={`scheduled-${s.id}`} className="p-3 flex items-center gap-3 bg-zinc-950">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-[10px] font-heading uppercase tracking-widest text-yellow-400">{s.stage}</span>
                          <span className="text-zinc-500 text-[10px]">·</span>
                          <span className="text-zinc-500 text-xs font-mono">{new Date(s.scheduled_at).toLocaleString("it-IT")}</span>
                          <span className="text-zinc-500 text-[10px]">·</span>
                          <span className={`text-[10px] font-heading uppercase tracking-widest ${
                            s.status === "pending" ? "text-zinc-300"
                            : s.status === "sent" ? "text-green-400"
                            : s.status === "canceled" ? "text-zinc-500"
                            : "text-yellow-400"
                          }`}>{sentBadge}</span>
                        </div>
                        <div className="text-zinc-300 text-xs truncate">{s.text}</div>
                      </div>
                      {isPending && (
                        <button
                          data-testid={`cancel-scheduled-${s.id}`}
                          onClick={() => cancelScheduled(s.id)}
                          className="p-1.5 text-red-400 hover:bg-red-500 hover:text-white border-2 border-zinc-800 hover:border-red-500 transition"
                          aria-label="Annulla"
                        >
                          <Trash2 className="w-4 h-4" strokeWidth={3} />
                        </button>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
          </div>

          <div className="mt-6 pt-5 border-t-2 border-zinc-800">
            <div className="font-heading uppercase text-white text-sm mb-2">Sync automatico</div>
            <ul className="text-xs text-zinc-400 space-y-1 leading-relaxed">
              <li>• I nuovi contatti generati dal chatbot vengono automaticamente pushati su Smshosting nel gruppo &quot;Lead Sito&quot;.</li>
              <li>• Gli SMS inviati dal CRM (vedi scheda contatto) passano dal gateway Smshosting.</li>
              <li>• Per importare in modo ricorrente i contatti del negozio fisico, ricarica e importa il gruppo &quot;Lista Card Daitanjack&quot; — i duplicati vengono mergiati per email/telefono.</li>
              <li>• Le campagne programmate vengono processate ogni 30 secondi da un worker dedicato — anche se chiudi il browser, l&apos;invio parte puntuale.</li>
            </ul>
          </div>
        </div>
      </section>
    </div>
  );
}
